/**
 * ADMIN A/B TESTS PAGE
 *
 * Create and manage A/B tests for:
 * - Features
 * - Pricing
 * - UI variations
 * - Flows
 *
 * Target by:
 * - Percentage
 * - Geography
 * - Plan
 * - Specific companies
 */

import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FlaskConical,
  Plus,
  Play,
  Pause,
  BarChart3,
  Users,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Clock,
  Target,
  Globe,
  Percent,
  Building2,
  ArrowRight,
  Trophy,
  XCircle,
} from 'lucide-react';

export const Route = createFileRoute('/admin/ab-tests/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
    const profile = context.auth.profile;
    if (!profile?.isSuperAdmin) {
      throw redirect({ to: '/dashboard', replace: true });
    }
  },
  component: AdminABTestsPage,
});

// Mock A/B tests data
const mockTests = [
  {
    id: '1',
    name: 'POS Pricing Test - Algeria',
    description: 'Testing higher price point for POS app in Algeria',
    type: 'pricing',
    status: 'running',
    targetType: 'geography',
    targetRegions: ['dz'],
    controlValue: { price: 5000 },
    variantValue: { price: 7500 },
    controlCount: 234,
    variantCount: 256,
    controlConversions: 45,
    variantConversions: 62,
    startDate: '2025-01-15',
    endDate: '2025-02-15',
  },
  {
    id: '2',
    name: 'Free Trial Length',
    description: '14 days vs 7 days free trial',
    type: 'feature',
    status: 'running',
    targetType: 'percentage',
    targetPercentage: 50,
    controlValue: { days: 14 },
    variantValue: { days: 7 },
    controlCount: 156,
    variantCount: 148,
    controlConversions: 32,
    variantConversions: 28,
    startDate: '2025-02-01',
    endDate: '2025-03-01',
  },
  {
    id: '3',
    name: 'Onboarding Flow',
    description: 'Simplified vs detailed onboarding',
    type: 'flow',
    status: 'completed',
    targetType: 'percentage',
    targetPercentage: 50,
    controlValue: { flow: 'detailed' },
    variantValue: { flow: 'simplified' },
    controlCount: 500,
    variantCount: 512,
    controlConversions: 125,
    variantConversions: 180,
    startDate: '2024-12-01',
    endDate: '2025-01-01',
    winner: 'variant',
  },
  {
    id: '4',
    name: 'Dashboard Layout',
    description: 'New dashboard design test',
    type: 'ui',
    status: 'draft',
    targetType: 'company_list',
    targetCompanies: ['company1', 'company2'],
    controlValue: { layout: 'current' },
    variantValue: { layout: 'new' },
    controlCount: 0,
    variantCount: 0,
    controlConversions: 0,
    variantConversions: 0,
  },
];

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'running':
      return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Running</Badge>;
    case 'paused':
      return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Paused</Badge>;
    case 'completed':
      return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Completed</Badge>;
    case 'draft':
      return <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">Draft</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

const getTypeBadge = (type: string) => {
  const colors: Record<string, string> = {
    'feature': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    'pricing': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'ui': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'flow': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };
  return <Badge className={colors[type] || 'bg-slate-500/20 text-slate-400'}>{type}</Badge>;
};

function TestCard({ test }: { test: typeof mockTests[0] }) {
  const totalControl = test.controlCount || 1;
  const totalVariant = test.variantCount || 1;
  const controlRate = ((test.controlConversions / totalControl) * 100).toFixed(1);
  const variantRate = ((test.variantConversions / totalVariant) * 100).toFixed(1);
  const isVariantBetter = parseFloat(variantRate) > parseFloat(controlRate);
  const improvement = (((parseFloat(variantRate) - parseFloat(controlRate)) / parseFloat(controlRate)) * 100).toFixed(1);

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {getTypeBadge(test.type)}
              {getStatusBadge(test.status)}
              {test.winner && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                  <Trophy className="w-3 h-3 mr-1" />
                  Winner: {test.winner}
                </Badge>
              )}
            </div>
            <CardTitle className="text-white">{test.name}</CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              {test.description}
            </CardDescription>
          </div>
          {test.status === 'running' && (
            <Button variant="ghost" size="sm" className="text-amber-400 hover:text-amber-300">
              <Pause className="w-4 h-4" />
            </Button>
          )}
          {test.status === 'draft' && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
              <Play className="w-4 h-4 mr-1" />
              Start
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Targeting info */}
        <div className="flex items-center gap-4 text-sm text-slate-400">
          {test.targetType === 'percentage' && (
            <div className="flex items-center gap-1">
              <Percent className="w-4 h-4" />
              {test.targetPercentage}% split
            </div>
          )}
          {test.targetType === 'geography' && (
            <div className="flex items-center gap-1">
              <Globe className="w-4 h-4" />
              {test.targetRegions?.join(', ').toUpperCase()}
            </div>
          )}
          {test.targetType === 'company_list' && (
            <div className="flex items-center gap-1">
              <Building2 className="w-4 h-4" />
              {test.targetCompanies?.length} companies
            </div>
          )}
          {test.startDate && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {test.startDate} - {test.endDate || 'ongoing'}
            </div>
          )}
        </div>

        {/* Results (only show for running/completed) */}
        {(test.status === 'running' || test.status === 'completed') && (
          <div className="space-y-3 p-4 rounded-lg bg-slate-700/30">
            {/* Control */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Control</span>
                <span className="text-white font-medium">{controlRate}% conversion</span>
              </div>
              <Progress value={parseFloat(controlRate)} className="h-2" />
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{test.controlCount.toLocaleString()} users</span>
                <span>{test.controlConversions} conversions</span>
              </div>
            </div>

            {/* Variant */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Variant</span>
                <span className="text-white font-medium">{variantRate}% conversion</span>
              </div>
              <Progress value={parseFloat(variantRate)} className="h-2 [&>div]:bg-violet-500" />
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{test.variantCount.toLocaleString()} users</span>
                <span>{test.variantConversions} conversions</span>
              </div>
            </div>

            {/* Summary */}
            <div className={`flex items-center gap-2 p-2 rounded ${isVariantBetter ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              {isVariantBetter ? (
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-400" />
              )}
              <span className={isVariantBetter ? 'text-emerald-400' : 'text-red-400'}>
                Variant is {isVariantBetter ? '+' : ''}{improvement}% {isVariantBetter ? 'better' : 'worse'}
              </span>
            </div>
          </div>
        )}

        {/* Values comparison */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 rounded-lg bg-slate-700/30">
            <p className="text-xs text-slate-400 mb-1">Control Value</p>
            <code className="text-sm text-slate-300">{JSON.stringify(test.controlValue)}</code>
          </div>
          <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/30">
            <p className="text-xs text-violet-400 mb-1">Variant Value</p>
            <code className="text-sm text-violet-300">{JSON.stringify(test.variantValue)}</code>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminABTestsPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredTests = mockTests.filter((test) => {
    return statusFilter === 'all' || test.status === statusFilter;
  });

  const stats = {
    total: mockTests.length,
    running: mockTests.filter(t => t.status === 'running').length,
    completed: mockTests.filter(t => t.status === 'completed').length,
    draft: mockTests.filter(t => t.status === 'draft').length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <FlaskConical className="w-8 h-8 text-violet-400" />
              A/B Tests
              <Badge className="bg-violet-500/20 text-violet-400 text-xs">Beta</Badge>
            </h1>
            <p className="text-slate-400 mt-2">
              Run experiments on features, pricing, UI, and flows
            </p>
          </div>
          <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Test
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/20">
                  <FlaskConical className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                  <p className="text-sm text-slate-400">Total Tests</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Play className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.running}</p>
                  <p className="text-sm text-slate-400">Running</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.completed}</p>
                  <p className="text-sm text-slate-400">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-500/20">
                  <Clock className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.draft}</p>
                  <p className="text-sm text-slate-400">Drafts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full">
            <TabsList className="bg-slate-800 border border-slate-700">
              <TabsTrigger value="all" className="data-[state=active]:bg-violet-600">All ({stats.total})</TabsTrigger>
              <TabsTrigger value="running" className="data-[state=active]:bg-violet-600">Running ({stats.running})</TabsTrigger>
              <TabsTrigger value="completed" className="data-[state=active]:bg-violet-600">Completed ({stats.completed})</TabsTrigger>
              <TabsTrigger value="draft" className="data-[state=active]:bg-violet-600">Drafts ({stats.draft})</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Tests Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredTests.map((test) => (
            <TestCard key={test.id} test={test} />
          ))}
        </div>

        {filteredTests.length === 0 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <FlaskConical className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No tests found</h3>
              <p className="text-slate-400 mb-4">Create your first A/B test to start experimenting</p>
              <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Test
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Test Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-violet-400" />
                Create New A/B Test
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Set up a new experiment to test features, pricing, or UI changes
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Test Name</Label>
                  <Input placeholder="e.g., POS Pricing Test" className="bg-slate-800 border-slate-700" />
                </div>
                <div className="space-y-2">
                  <Label>Test Type</Label>
                  <Select defaultValue="feature">
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="feature">Feature</SelectItem>
                      <SelectItem value="pricing">Pricing</SelectItem>
                      <SelectItem value="ui">UI</SelectItem>
                      <SelectItem value="flow">Flow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea placeholder="Describe what you're testing..." className="bg-slate-800 border-slate-700" />
              </div>

              <div className="space-y-4">
                <Label>Targeting</Label>
                <Select defaultValue="percentage">
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="percentage">
                      <div className="flex items-center gap-2">
                        <Percent className="w-4 h-4" />
                        Percentage Split
                      </div>
                    </SelectItem>
                    <SelectItem value="geography">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Geography
                      </div>
                    </SelectItem>
                    <SelectItem value="plan">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Plan
                      </div>
                    </SelectItem>
                    <SelectItem value="company_list">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Specific Companies
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Control Value (JSON)</Label>
                  <Textarea
                    placeholder='{"price": 5000}'
                    className="bg-slate-800 border-slate-700 font-mono text-sm"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Variant Value (JSON)</Label>
                  <Textarea
                    placeholder='{"price": 7500}'
                    className="bg-slate-800 border-slate-700 font-mono text-sm"
                    rows={3}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" className="bg-slate-800 border-slate-700" />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" className="bg-slate-800 border-slate-700" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="border-slate-700">
                Cancel
              </Button>
              <Button variant="outline" className="border-slate-700">
                Save as Draft
              </Button>
              <Button className="bg-violet-600 hover:bg-violet-700">
                <Play className="w-4 h-4 mr-2" />
                Create & Start
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminABTestsPage;
